import {
  FIVE9_UPSERT_CALL,
  FIVE9_SET_ACTIVE_CALL,
  FIVE9_ASSOCIATE_DISPATCH,
  FIVE9_CLEAR_CALL,
  FIVE9_LOG_APPEND,
  FIVE9_LOG_CLEAR,
  FIVE9_HYDRATE,
  FIVE9_RESET,
  FIVE9_SET_CLICK_TO_DIAL
} from 'src/actions/five9Actions';
import { looksLikeCallSid } from 'src/utils/five9/callSidValidation';
import stripPersistableCall from 'src/utils/five9/persist';

/**
 * @typedef {import('src/type-defs/five9Types').Five9Call} Five9Call
 * @typedef {import('src/type-defs/five9Types').Five9LogEntry} Five9LogEntry
 * @typedef {import('src/type-defs/five9Types').Five9SliceState} Five9SliceState
 * @typedef {import('src/type-defs/five9Types').Five9PersistedSnapshot} Five9PersistedSnapshot
 */

/** Schema version for the persisted subset written to sessionStorage.
 *  Bump when {@link Five9PersistedSnapshot} adds a required field so
 *  older blobs are safely discarded on load. @type {number} */
export const PERSIST_SCHEMA_VERSION = 1;

/** Maximum entries retained in the `log` ring buffer. @type {number} */
export const LOG_CAP = 100;

/** @type {Five9SliceState} */
const initialState = {
  calls: {},
  activeCallSid: null,
  clickToDialEnabled: false,
  log: [],
  logCap: LOG_CAP
};

/**
 * Selector projecting only the fields that should survive a hard reload via
 * sessionStorage and cross-tab broadcasts. Each call is passed through
 * `stripPersistableCall` so raw SDK payloads never leave the runtime into
 * storage or BroadcastChannel messages. The `log` slice is also excluded —
 * logs are session-in-memory only.
 *
 * @param {{ five9?: Five9SliceState }} state
 * @returns {Five9PersistedSnapshot}
 */
export const selectPersistableFive9 = (state) => {
  const calls = state?.five9?.calls || {};
  const ownerUserId = state?.account?.user?.id ?? null;
  /** @type {Record<string, Five9Call>} */
  const slim = {};
  Object.keys(calls).forEach((k) => {
    slim[k] = stripPersistableCall(calls[k]);
  });
  return {
    v: PERSIST_SCHEMA_VERSION,
    ownerUserId,
    calls: slim,
    activeCallSid: state?.five9?.activeCallSid || null,
    clickToDialEnabled: !!state?.five9?.clickToDialEnabled
  };
};

/**
 * @param {Five9LogEntry[]} log
 * @param {number} cap
 * @returns {Five9LogEntry[]}
 */
const trimLog = (log, cap) => (
  log.length <= cap ? log : log.slice(log.length - cap)
);

/**
 * @param {Record<string, Five9Call>|undefined} calls
 * @returns {Record<string, Five9Call>}
 */
const sanitizeCallsMap = (calls) => {
  if (!calls || typeof calls !== 'object') return {};
  /** @type {Record<string, Five9Call>} */
  const out = {};
  Object.keys(calls).forEach((k) => {
    if (looksLikeCallSid(k)) out[k] = calls[k];
  });
  return out;
};

/**
 * Return `incoming` with any `null` / `undefined` fields removed, so a
 * partial update never wipes out fields the previous event supplied. This
 * matters because different Five9 SDK callbacks/events carry different
 * subsets of the call's metadata (e.g. `interactionOpened` has only
 * `interactionId`; `callStarted` has full `callData`; `saveLog` has
 * `interactionData` with `callState: FINISHED` but no ANI). Without this
 * filter, the badge in the log panel flaps between the ANI (from
 * `callStarted`) and the raw sid (from the low-info follow-ups).
 *
 * @param {Record<string, any>} obj
 * @returns {Record<string, any>}
 */
const compact = (obj) => {
  /** @type {Record<string, any>} */
  const out = {};
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v !== null && v !== undefined) out[k] = v;
  });
  return out;
};

/**
 * @param {Five9Call|undefined} a
 * @param {Five9Call|undefined} b
 * @returns {boolean}
 */
const shallowEqualCall = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (let i = 0; i < ka.length; i += 1) {
    const k = ka[i];
    // `raw` is intentionally left as reference-equality — the SDK gives us
    // a fresh payload object every event, so a deep compare is not worth it.
    if (a[k] !== b[k]) return false;
  }
  return true;
};

/**
 * Reducer for the `five9` slice.
 *
 * @param {Five9SliceState} [state]
 * @param {{ type: string, payload?: any }} action
 * @returns {Five9SliceState}
 */
export default function five9Reducer(state = initialState, action) {
  switch (action.type) {
    case FIVE9_UPSERT_CALL: {
      const incoming = action.payload || {};
      if (!incoming.callSid) return state;
      const existing = state.calls[incoming.callSid] || {};
      const now = Date.now();
      const merged = {
        startedAt: existing.startedAt || now,
        ...existing,
        ...compact(incoming),
        lastEventAt: now
      };
      // If nothing meaningful changed (only `lastEventAt` moved forward),
      // skip the state churn to avoid unnecessary re-renders / broadcasts.
      const existingWithoutTs = { ...existing, lastEventAt: now };
      if (shallowEqualCall(existingWithoutTs, merged)) return state;
      return {
        ...state,
        calls: { ...state.calls, [incoming.callSid]: merged }
      };
    }
    case FIVE9_SET_ACTIVE_CALL: {
      const { callSid } = action.payload || {};
      // Reject non-callSid-looking values (name-object refs, agent IDs, etc.)
      const safe = looksLikeCallSid(callSid) ? callSid : null;
      if (state.activeCallSid === safe) return state;
      return { ...state, activeCallSid: safe };
    }
    case FIVE9_ASSOCIATE_DISPATCH: {
      const { callSid, dispatchId } = action.payload || {};
      const target = callSid || state.activeCallSid;
      if (!target || !state.calls[target]) return state;
      return {
        ...state,
        calls: {
          ...state.calls,
          [target]: {
            ...state.calls[target],
            associatedDispatchId: dispatchId,
            lastEventAt: Date.now()
          }
        }
      };
    }
    case FIVE9_CLEAR_CALL: {
      const { callSid } = action.payload || {};
      if (!callSid || !state.calls[callSid]) return state;
      const { [callSid]: _removed, ...rest } = state.calls;
      return {
        ...state,
        calls: rest,
        activeCallSid: state.activeCallSid === callSid ? null : state.activeCallSid
      };
    }
    case FIVE9_SET_CLICK_TO_DIAL: {
      const enabled = !!(action.payload && action.payload.enabled);
      if (state.clickToDialEnabled === enabled) return state;
      return { ...state, clickToDialEnabled: enabled };
    }
    case FIVE9_LOG_APPEND: {
      const entries = action.payload || [];
      if (!entries.length) return state;
      const nextLog = trimLog([...state.log, ...entries], state.logCap);
      return { ...state, log: nextLog };
    }
    case FIVE9_LOG_CLEAR:
      return { ...state, log: [] };
    case FIVE9_RESET:
      // Auth-transition purge: return to initial state so a previous user's
      // calls, active-sid, dispatch associations, and log can never leak
      // into a subsequent authenticated session in the same tab.
      return { ...initialState };
    case FIVE9_HYDRATE: {
      const snap = action.payload || {};
      if (snap.v !== PERSIST_SCHEMA_VERSION) return state;
      const nextCalls = sanitizeCallsMap(snap.calls);
      const hasActiveCallSid = Object.prototype.hasOwnProperty.call(snap, 'activeCallSid');
      const nextActiveRaw = hasActiveCallSid ? snap.activeCallSid : state.activeCallSid;
      const nextActive = looksLikeCallSid(nextActiveRaw) && nextCalls[nextActiveRaw]
        ? nextActiveRaw
        : null;
      // No-op only when EVERY per-call value shallow-matches. Comparing
      // just the key set (prior implementation) would silently drop
      // in-place updates from peer tabs — e.g. a peer setting
      // `associatedDispatchId` or advancing `state` without changing the
      // set of active callSids.
      const curKeys = Object.keys(state.calls);
      const newKeys = Object.keys(nextCalls);
      const callsMatch = curKeys.length === newKeys.length
        && newKeys.every((k) => shallowEqualCall(state.calls[k], nextCalls[k]));
      // Older snapshots predate clickToDialEnabled — leave local value alone.
      const nextClickToDial = Object.prototype.hasOwnProperty.call(snap, 'clickToDialEnabled')
        ? !!snap.clickToDialEnabled
        : state.clickToDialEnabled;
      if (state.activeCallSid === nextActive && callsMatch
        && state.clickToDialEnabled === nextClickToDial) return state;
      return {
        ...state,
        calls: nextCalls,
        activeCallSid: nextActive,
        clickToDialEnabled: nextClickToDial
      };
    }
    default:
      return state;
  }
}
